<template>
  <section class="int-section">
    <div class="int-section__accent" />
    <div class="int-section__inner">
      <header class="int-section__head">
        <div class="int-section__head-left">
          <div class="int-section__icon" aria-hidden="true">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h2 class="int-section__title">{{ t('integracao.agendamentoForms.sectionTitle') }}</h2>
            <p class="int-section__desc">{{ t('integracao.agendamentoForms.sectionDesc') }}</p>
          </div>
        </div>
        <button type="button" class="int-section__cta" @click="openCreate">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" /></svg>
          {{ t('integracao.agendamentoForms.newForm') }}
        </button>
      </header>

      <div v-if="loading" class="int-section__loading"><span class="int-section__spinner" />{{ t('common.loading') }}</div>
      <div v-else-if="!forms.length" class="int-section__empty"><p>{{ t('integracao.agendamentoForms.empty') }}</p></div>
      <div v-else class="int-item-list">
        <article v-for="f in forms" :key="f.id" class="int-item-card">
          <div class="int-item-card__main">
            <h3 class="int-item-card__title">{{ f.nome }}</h3>
            <div class="int-item-card__meta">
              <span :class="['int-badge', f.ativo ? 'int-badge--on' : 'int-badge--off']">
                <span class="int-badge__dot" />{{ f.ativo ? t('integracao.forms.active') : t('integracao.forms.inactive') }}
              </span>
            </div>
          </div>
          <div class="int-item-card__actions">
            <button type="button" class="int-action-btn int-action-btn--primary" @click="copyUrl(f.publicToken)">{{ t('integracao.forms.copyUrl') }}</button>
            <button type="button" class="int-action-btn" @click="copyIframe(f.publicToken)">{{ t('integracao.forms.copyIframe') }}</button>
            <button type="button" class="int-action-btn" @click="openEdit(f)">{{ t('common.edit') }}</button>
            <button type="button" class="int-action-btn int-action-btn--danger" @click="removeForm(f)">{{ t('common.delete') }}</button>
          </div>
        </article>
      </div>
    </div>

    <AgendamentoFormEditorModal
      :open="editorOpen"
      :initial="editing"
      :saving="saving"
      :workspace-id="workspaceId"
      :workspace-tipo="workspaceTipo"
      :motivos="motivosAgendamento"
      @close="editorOpen = false"
      @save="onSave"
    />
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import api from '@/services/api'
import AgendamentoFormEditorModal from './AgendamentoFormEditorModal.vue'
import { publicAgendamentoFormUrl, agendamentoIframeSnippet } from '@/utils/agendamentoFormFieldConfig'
import '@/assets/styles/integration-section.css'

const props = defineProps({
  workspaceId: { type: Number, required: true },
  workspaceTipo: { type: String, default: 'SERVICOS' }
})
const { t } = useI18n()
const toast = useToast()

const forms = ref([])
const motivosAgendamento = ref([])
const loading = ref(false)
const editorOpen = ref(false)
const editing = ref(null)
const saving = ref(false)

async function loadMotivos () {
  try {
    const { data } = await api.get(`/workspace-config/${props.workspaceId}/motivos-agendamento`)
    motivosAgendamento.value = Array.isArray(data) ? data : []
  } catch {
    motivosAgendamento.value = []
  }
}

async function load () {
  loading.value = true
  try {
    await loadMotivos()
    const { data } = await api.get(`/workspace/${props.workspaceId}/agendamento-forms`)
    forms.value = Array.isArray(data) ? data : []
  } catch (e) {
    toast.error(e.response?.data?.error || t('integracao.agendamentoForms.loadError'))
  } finally {
    loading.value = false
  }
}

function openCreate () { editing.value = null; editorOpen.value = true }
function openEdit (f) { editing.value = f; editorOpen.value = true }

async function onSave (payload) {
  saving.value = true
  try {
    if (editing.value?.id) {
      await api.put(`/workspace/${props.workspaceId}/agendamento-forms/${editing.value.id}`, payload)
      toast.success(t('integracao.agendamentoForms.updated'))
    } else {
      await api.post(`/workspace/${props.workspaceId}/agendamento-forms`, payload)
      toast.success(t('integracao.agendamentoForms.created'))
    }
    editorOpen.value = false
    await load()
  } catch (e) {
    toast.error(e.response?.data?.error || t('integracao.agendamentoForms.saveError'))
  } finally {
    saving.value = false
  }
}

async function removeForm (f) {
  if (!confirm(t('integracao.agendamentoForms.deleteConfirm'))) return
  try {
    await api.delete(`/workspace/${props.workspaceId}/agendamento-forms/${f.id}`)
    toast.success(t('integracao.agendamentoForms.deleted'))
    await load()
  } catch (e) {
    toast.error(e.response?.data?.error || t('integracao.agendamentoForms.deleteError'))
  }
}

async function copyUrl (token) {
  await navigator.clipboard.writeText(publicAgendamentoFormUrl(token))
  toast.success(t('integracao.forms.copied'))
}

async function copyIframe (token) {
  await navigator.clipboard.writeText(agendamentoIframeSnippet(token))
  toast.success(t('integracao.forms.copied'))
}

onMounted(load)
</script>
